import {
  SAVED_FILTER_SET_TYPES,
  SAVED_FILTER_SET_VISIBILITIES
} from '@linearb/linjs-common';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FILTER_SET_MODEL } from '../FilterSet.model';
import { myAccountSelector } from 'state/selectors/account';
import { useSelector } from 'react-redux';

const VALIDATION_MESSAGES = {
  NAME_EXISTS: 'Name already exists',
  NAME_TOO_LONG: 'Name has to be less than 40 characters',
  EMPTY_NAME: 'Name is required',
  INVALID_TYPE: `Type must be one of ${Object.values(
    SAVED_FILTER_SET_TYPES
  ).join(', ')}`,
  INVALID_VISIBILITY: `Visibility must be one of ${Object.values(
    SAVED_FILTER_SET_VISIBILITIES
  ).join(', ')}`,
  DUE_DATE_REQUIRED: 'Target date is mandatory for projects',
  INVALID_TARGET_DATE: 'Target date should be greater than today'
};

const useFormValidator = ({ Context, formState }) => {
  const {
    state: { filterSets }
  } = useContext(Context);

  const isInitialRender = useRef(true);

  const account = useSelector(myAccountSelector);

  const filterSetNamesByType = useMemo(
    () =>
      Object.values(filterSets).reduce((acc, filterSet) => {
        if (!acc[filterSet.type]) {
          acc[filterSet.type] = [];
        }
        if (
          account?.id === filterSet.account_id &&
          filterSet.id !== formState.id
        ) {
          acc[filterSet.type].push(filterSet.name);
        }
        return acc;
      }, {}),
    [filterSets]
  );

  const [validationError, setValidationError] = useState(null);

  const getValidationError = () => {
    const { name, type, visibility, due_date } = formState;

    if (!name || name === '') {
      return {
        type: FILTER_SET_MODEL.NAME,
        message: VALIDATION_MESSAGES.EMPTY_NAME
      };
    }
    if (name.length > 40) {
      return {
        type: FILTER_SET_MODEL.NAME,
        message: VALIDATION_MESSAGES.NAME_TOO_LONG
      };
    }
    if (
      filterSetNamesByType?.[type]
        ?.map(n => n.toLowerCase())
        ?.includes(name.toLowerCase())
    ) {
      return {
        type: FILTER_SET_MODEL.NAME,
        message: VALIDATION_MESSAGES.NAME_EXISTS
      };
    }
    if (!Object.values(SAVED_FILTER_SET_TYPES).includes(type)) {
      return {
        type: FILTER_SET_MODEL.TYPE,
        message: VALIDATION_MESSAGES.INVALID_TYPE
      };
    }
    if (!Object.values(SAVED_FILTER_SET_VISIBILITIES).includes(visibility)) {
      return {
        type: FILTER_SET_MODEL.VISIBILITY,
        message: VALIDATION_MESSAGES.INVALID_VISIBILITY
      };
    }
    if (type === SAVED_FILTER_SET_TYPES.PROJECT && !due_date) {
      return {
        type: FILTER_SET_MODEL.METADATA.DUE_DATE,
        message: VALIDATION_MESSAGES.DUE_DATE_REQUIRED
      };
    }
    if (due_date && new Date(due_date) < new Date()) {
      return {
        type: FILTER_SET_MODEL.METADATA.DUE_DATE,
        message: VALIDATION_MESSAGES.INVALID_TARGET_DATE
      };
    }
    return null;
  };

  const validate = () => {
    const validation = getValidationError();
    setValidationError(validation);
    return validation;
  };

  useEffect(() => {
    // avoid validation when first opening the form
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    validate();
  }, [
    formState[FILTER_SET_MODEL.TYPE],
    formState[FILTER_SET_MODEL.METADATA.DUE_DATE]
  ]);

  return { validate, validationError, isValid: !validationError };
};

export default useFormValidator;
